import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';
import { SchemaSection } from '@rossum/api-client/schemas';
import { Tile } from '@rossum/components/tile';
import { FeatureIcon } from '@rossum/ui';
import {
  IconCategory,
  IconCode,
  IconCustomExport,
  IconDatabase,
  IconFilter,
  IconGitMerge,
  IconLayoutGrid,
  IconLink,
  IconList,
  IconNumber,
  IconSearch,
  IconSortAscending,
  IconTransform,
  IconTrash,
  TablerIcon,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
} from '@rossum/ui/material';
import { aiFeaturesEnabledSelector } from '../../../../../../redux/modules/organizationGroup/selectors';
import { datasetTablePath } from '../../../../../datasets/routes';
import { Summary } from '../../../../../formulas/FormulaFieldSection/components/Summary';
import { useComputedFieldSummary } from './hooks/useComputedFieldSummary';
import { QueryVariablesCard } from './QueryVariablesCard';
import { QueryObject, VariablesMap } from './types';
import { filterSchemaContentForQuery } from './utils/filterSchemaContentForQuery';

const KNOWN_KEYWORDS = [
  'search',
  'filter',
  'projection',
  'lookup',
  'transform',
  'sort',
  'limit',
  'group',
  'unwind',
  'merge',
  'facet',
] as const;

const knownKeywordSchema = z.enum(KNOWN_KEYWORDS);

type KnownKeyword = z.infer<typeof knownKeywordSchema>;

const keywordIconMap: Record<KnownKeyword, TablerIcon> = {
  search: IconSearch,
  filter: IconFilter,
  projection: IconCustomExport,
  lookup: IconLink,
  transform: IconTransform,
  sort: IconSortAscending,
  limit: IconNumber,
  group: IconCategory,
  unwind: IconList,
  merge: IconGitMerge,
  facet: IconLayoutGrid,
};

const DEFAULT_ICON = IconCode;

const getKeywordIcon = (keyword: string) => {
  const result = knownKeywordSchema.safeParse(keyword);
  return result.success ? keywordIconMap[result.data] : DEFAULT_ICON;
};

const isEmptyQuery = (query: QueryObject): boolean =>
  !query.aggregate ||
  (Array.isArray(query.aggregate) && query.aggregate.length === 0);

type QuerySummaryTileProps = {
  query: QueryObject;
  queryIndex: number;
  schemaContent: SchemaSection[];
  fieldSchemaId: string;
  variables: VariablesMap;
  disabled?: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onDelete: () => void;
  onEdit: () => void;
  dataset: string;
  datasetName?: string;
};

export const QuerySummaryTile = ({
  query,
  queryIndex,
  schemaContent,
  fieldSchemaId,
  variables,
  disabled,
  isExpanded,
  onToggleExpanded,
  onDelete,
  onEdit,
  dataset,
  datasetName,
}: QuerySummaryTileProps) => {
  const intl = useIntl();
  const aiFeaturesEnabled = useSelector(aiFeaturesEnabledSelector);

  // Build schemaContent with only this single query for the summary API
  // Use the `query` prop directly instead of extracting by index from schemaContent
  // to avoid stale data when indices shift after deletion
  const filteredSchemaContent = useMemo(
    () => filterSchemaContentForQuery(schemaContent, fieldSchemaId, query),
    [schemaContent, fieldSchemaId, query]
  );

  const isEmpty = isEmptyQuery(query);

  const {
    data: summaries,
    isFetching,
    isSuccess,
  } = useComputedFieldSummary(
    {
      schemaContent: filteredSchemaContent,
      fieldSchemaId,
    },
    {
      enabled: !disabled && !isEmpty && aiFeaturesEnabled,
      query,
      variables,
    }
  );

  // Since we filtered to a single query, the summary is at index 0
  const querySummary = summaries?.[0];

  const isWaitingForSummary = !isSuccess && isFetching;

  const queryTitle = isWaitingForSummary ? (
    <Skeleton variant="text" width={200} />
  ) : isEmpty ? (
    intl.formatMessage({
      id: 'features.queueSettings.fields.lookup.emptyQuery.title',
    })
  ) : (
    querySummary?.title
  );

  const querySubtitle = isWaitingForSummary ? (
    <Skeleton variant="text" width={300} height={16} />
  ) : isEmpty ? (
    intl.formatMessage({
      id: 'features.queueSettings.fields.lookup.emptyQuery.subtitle',
    })
  ) : (
    querySummary?.subtitle
  );

  return (
    <Tile
      iconProps={{
        content: queryIndex + 1,
        color: isEmpty ? 'blank' : 'aurora',
        size: 'large',
      }}
      title={queryTitle}
      description={querySubtitle}
      disabled={disabled}
      quickActions={[
        {
          key: 'delete',
          iconProps: {
            Icon: IconTrash,
            state: false,
            disabled: false,
          },
          tooltip: intl.formatMessage({
            id: 'features.queueSettings.fields.lookup.builtin.complexLookup.deleteQuery',
          }),
          onClick: onDelete,
        },
      ]}
      expandable={{
        expanded: isExpanded,
        onToggleExpanded,
        content: isWaitingForSummary ? (
          <Stack spacing={2} sx={{ p: 2 }}>
            <QueryVariablesCard query={query} variables={variables} />
            <Skeleton variant="rounded" height={60} />
            <Skeleton variant="rounded" height={60} />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ p: 2 }}>
            <Card
              variant="outlined"
              sx={{
                backgroundColor: t =>
                  t.palette.mode === 'dark' ? '#1A1F27' : '#F9FAFB',
              }}
            >
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between">
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <FeatureIcon
                      content={IconDatabase}
                      size="small"
                      color="aurora"
                    />
                    <Stack spacing={0.5}>
                      <Chip
                        label={datasetName || dataset}
                        size="small"
                        component={RouterLink}
                        to={datasetTablePath(dataset)}
                        clickable
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
            <QueryVariablesCard query={query} variables={variables} />
            {querySummary?.blocks.map(block => {
              return (
                <Card
                  key={`${block.keyword}-${block.summary.slice(0, 10)}`}
                  variant="outlined"
                  sx={{
                    // TODO: Define Paper2 in theme and reuse here
                    backgroundColor: t =>
                      t.palette.mode === 'dark' ? '#1A1F27' : '#F9FAFB',
                  }}
                >
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                      >
                        <FeatureIcon
                          content={getKeywordIcon(block.keyword)}
                          size="small"
                          color="aurora"
                        />
                        <Stack spacing={0.5}>
                          <Summary content={block.summary} />
                        </Stack>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
            {aiFeaturesEnabled ? (
              <Button
                variant="outlined"
                color="aurora"
                sx={{ alignSelf: 'flex-end' }}
                onClick={() => onEdit()}
              >
                {intl.formatMessage({
                  id: 'features.queueSettings.fields.lookup.builtin.complexLookup.editLookup',
                })}
              </Button>
            ) : null}
          </Stack>
        ),
      }}
    />
  );
};
